const { getElement } = window.BAO.Utils

export class DrawerBackInStock extends customElements.get('site-drawer') {
  connectedCallback () {
    this.cacheElements()
    this.sizeTemplate =
      this.sizeEl?.getAttribute('data-size-template') || 'Size ___SIZE___'

    super.connectedCallback()
  }

  setupListeners () {
    super.setupListeners()

    this.cacheElements()

    this.handleOpen = this.handleOpen.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.BACK_IN_STOCK.OPEN,
      this.handleOpen,
    )

    if (this.formEl) {
      this.listeners.add(this.formEl, 'submit', this.handleSubmit)
    }
  }

  cacheElements () {
    this.body = getElement('[data-drawer-back-in-stock-el="body"]', {
      context: this,
    })
    this.sizeEl = getElement('[data-drawer-back-in-stock-el="size"]', {
      context: this,
    })
    this.formEl = getElement('[data-drawer-back-in-stock-el="form"]', {
      context: this,
    })
    this.variantInput = getElement(
      '[data-drawer-back-in-stock-el="variant-id"]',
      { context: this },
    )
    this.emailInput = getElement('[data-drawer-back-in-stock-el="email"]', {
      context: this,
    })
    this.marketingInput = getElement(
      '[data-drawer-back-in-stock-el="marketing"]',
      { context: this },
    )
    this.messageEl = getElement('[data-drawer-back-in-stock-el="message"]', {
      context: this,
    })
    this.submitEl = getElement('[data-drawer-back-in-stock-el="submit"]', {
      context: this,
    })
  }

  handleOpen (event) {
    const { variantId, productId } = event.detail || {}

    if (!variantId) return

    this.syncVariant(variantId, productId)
    this.clearMessage()
    this.clearSentTimeout()
    this.setLoading(false)
    this.setButtonLabel('default')
    this.openDrawer()

    window.requestAnimationFrame(() => {
      this.emailInput?.focus()
    })
  }

  syncVariant (variantId, productId) {
    if (this.variantInput) {
      this.variantInput.value = String(variantId)
    }

    if (productId) {
      this.setAttribute('data-product-id', String(productId))
    }

    const variant = this.getVariantById(variantId)
    if (!variant || !this.sizeEl) return

    const sizeLabel = this.getSizeLabel(variant)
    if (!sizeLabel) return

    this.sizeEl.textContent = this.sizeTemplate.replace('___SIZE___', sizeLabel)
  }

  getProductJson () {
    const productJsonEl = getElement('[data-product-json]')
    if (!productJsonEl) return null

    try {
      return JSON.parse(productJsonEl.textContent)
    } catch (error) {
      return null
    }
  }

  getVariantById (variantId) {
    const product = this.getProductJson()
    if (!product?.variants) return null

    return product.variants.find(
      (variant) => String(variant.id) === String(variantId),
    )
  }

  getSizeLabel (variant) {
    const product = this.getProductJson()
    const sizeOptionIndex = product?.options?.findIndex((option) =>
      String(option).toLowerCase().includes('size'),
    )

    let sizeLabel = String(variant.title || '')
      .split('/')
      .pop()
      .trim()

    if (sizeOptionIndex >= 0 && variant.options?.[sizeOptionIndex]) {
      sizeLabel = variant.options[sizeOptionIndex]
    }

    const sizeLabelDowncased = sizeLabel.toLowerCase().trim()
    if (
      sizeLabelDowncased === 'one size' ||
      sizeLabelDowncased === 'onesize' ||
      sizeLabelDowncased === 'os'
    ) {
      return ''
    }

    return sizeLabel
  }

  async handleSubmit (event) {
    event.preventDefault()

    const email = this.emailInput?.value?.trim()
    const variantId = Number(this.variantInput?.value)
    const productId = Number(this.getAttribute('data-product-id'))
    const acceptsMarketing = this.marketingInput?.checked === true

    this.clearMessage()
    this.clearSentTimeout()

    if (!email || !variantId || !productId) {
      this.setButtonLabel('default')
      this.showMessage(
        this.formEl?.dataset.errorEmail ||
          'Please enter a valid email address.',
        true,
      )
      return
    }

    this.setLoading(true)
    this.setButtonLabel('sending')

    const ready = await this.waitForBis()
    if (!ready) {
      this.setLoading(false)
      this.setButtonLabel('default')
      this.showMessage(
        this.formEl?.dataset.errorGeneric ||
          'Unable to sign up right now. Please try again.',
        true,
      )
      return
    }

    try {
      const data = await this.createBisNotification(
        email,
        variantId,
        productId,
        acceptsMarketing,
      )
      const status = String(data?.status || '').toUpperCase()

      if (status === 'OK') {
        this.showMessage(data.message, false)
        this.emailInput.value = ''
        if (this.marketingInput) {
          this.marketingInput.checked = false
        }

        this.setButtonLabel('sent')
        this.sentTimeout = window.setTimeout(() => {
          this.setButtonLabel('default')
          this.setLoading(false)
        }, 2000)
      } else {
        this.setButtonLabel('default')
        this.setLoading(false)
        this.showMessage(this.getBisErrorMessage(data), true)
      }
    } catch (error) {
      this.setButtonLabel('default')
      this.setLoading(false)
      this.showMessage(
        this.formEl?.dataset.errorGeneric ||
          'Unable to sign up right now. Please try again.',
        true,
      )
    }
  }

  createBisNotification (email, variantId, productId, acceptsMarketing) {
    return new Promise((resolve, reject) => {
      try {
        const bisPromise =
          acceptsMarketing === true
            ? window.BIS.create(email, variantId, productId, {
                accepts_marketing: true,
              })
            : window.BIS.create(email, variantId, productId)

        if (!bisPromise || typeof bisPromise.then !== 'function') {
          reject(new Error('BIS.create did not return a promise'))
          return
        }

        bisPromise.then((data) => resolve(data))
      } catch (error) {
        reject(error)
      }
    })
  }

  getBisErrorMessage (data) {
    if (data?.errors) {
      const messages = []

      Object.keys(data.errors).forEach((key) => {
        const fieldErrors = data.errors[key]
        if (Array.isArray(fieldErrors)) {
          messages.push(...fieldErrors)
        } else if (fieldErrors) {
          messages.push(String(fieldErrors))
        }
      })

      if (messages.length > 0) {
        return messages.join(' ')
      }
    }

    return (
      data?.message ||
      this.formEl?.dataset.errorGeneric ||
      'Unable to sign up right now. Please try again.'
    )
  }

  waitForBis (attempts = 10) {
    return new Promise((resolve) => {
      let remaining = attempts

      const check = () => {
        if (typeof window.BIS !== 'undefined' && typeof window.BIS.create === 'function') {
          resolve(true)
          return
        }

        remaining -= 1
        if (remaining <= 0) {
          resolve(false)
          return
        }

        window.setTimeout(check, 500)
      }

      check()
    })
  }

  setButtonLabel (state) {
    if (!this.submitEl) return

    const labels = {
      default: this.submitEl.dataset.labelDefault || 'Notify me',
      sending: this.submitEl.dataset.labelSending || 'Sending...',
      sent: this.submitEl.dataset.labelSent || 'Sent',
    }

    this.submitEl.textContent = labels[state] || labels.default
  }

  clearSentTimeout () {
    if (!this.sentTimeout) return

    window.clearTimeout(this.sentTimeout)
    this.sentTimeout = null
  }

  setLoading (isLoading) {
    if (!this.submitEl) return

    this.submitEl.disabled = isLoading
    this.submitEl.setAttribute('aria-busy', String(isLoading))
  }

  showMessage (message, isError) {
    if (!this.messageEl || !message) return

    this.messageEl.hidden = false
    this.messageEl.removeAttribute('hidden')
    this.messageEl.textContent = message
    this.messageEl.classList.toggle('drw-BackInStock_Message-error', isError)
    this.messageEl.classList.toggle('drw-BackInStock_Message-ok', !isError)
  }

  clearMessage () {
    if (!this.messageEl) return

    this.messageEl.hidden = true
    this.messageEl.setAttribute('hidden', '')
    this.messageEl.textContent = ''
    this.messageEl.classList.remove(
      'drw-BackInStock_Message-error',
      'drw-BackInStock_Message-ok',
    )
  }

  openDrawer () {
    getElement('site-drawers')?.open(this.getAttribute('key'))
  }

  closeDrawer () {
    getElement('site-drawers')?.closeDrawer(this.getAttribute('key'))
  }
}

customElements.define('drawer-back-in-stock', DrawerBackInStock)
